import { DocumentViewer } from '@aecom-ai-engineering/react-components';
import { useReadUrl } from '@consigli/hooks';
import { type Document, DocumentType, useConvertFindingToDocument } from '@consigli/types';
import { type FC, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { isOptimizationService } from '@/features/document-viewer/document-preview';
import { toExcelHighlight, toHighlights } from '@/features/document-viewer/excel-highlight';
import { createRenderers } from '@/features/document-viewer/renderers';
import { useViewer } from '@/features/document-viewer/use-viewer';
import { ViewerMode, useViewerContext } from '@/features/document-viewer/use-viewer-context';
import { useFindingsContextSafe } from '@/pages/insight/findings/findings-context';
import {
  isExcelDocumentType,
  isHTMLDocumentType,
  isIFCDocumentType,
  isMsWordType,
  resolveDocumentFileType,
  resolveDocumentSrc,
} from '@/util/document-check';

/**
 * Optimization services render Word documents client-side even when a
 * converted PDF exists; everywhere else a converted or finding Word document
 * is shown as the PDF its `fileUrl` points at.
 */
const resolveFileType = (document: Document, serviceName: string | undefined) => {
  if (isHTMLDocumentType(document.name) || document.extension === '.html') return 'html';
  if (isIFCDocumentType(document.name)) return 'ifc';
  if (isExcelDocumentType(document.name)) return 'excel';
  if (isMsWordType(document.name)) {
    if (isOptimizationService(serviceName)) return undefined;
    if (document.convertedPdfFileId || document.type === DocumentType.FINDING) return 'pdf';
    return undefined;
  }
  return resolveDocumentFileType(document);
};

interface FullscreenDocumentViewerProps {
  document: Document;
  src: string;
}

const FullscreenDocumentViewer: FC<FullscreenDocumentViewerProps> = ({ document, src }) => {
  const { t } = useTranslation();
  const { serviceName } = useParams();
  const { setDocument, setMode } = useViewerContext();
  const { close, navigateToDocument, canNavigatePrevious, canNavigateNext } = useViewer();
  const findingsContext = useFindingsContextSafe();
  const convertFindingToDocument = useConvertFindingToDocument(t);

  const isFinding = document.type === DocumentType.FINDING;
  const isExcel = isExcelDocumentType(document.name);

  const renderers = useMemo(
    () =>
      isFinding
        ? createRenderers({
            pdfHighlights: isExcel ? undefined : toHighlights(document.findingCoordinates),
            initialPage: isExcel ? undefined : (document.pageNumber ?? 0) + 1,
            excelHighlight: isExcel ? toExcelHighlight(document.findingCellCoordinates) : undefined,
          })
        : createRenderers(),
    [
      isFinding,
      isExcel,
      document.findingCoordinates,
      document.pageNumber,
      document.findingCellCoordinates,
    ],
  );

  const navigateFinding = useCallback(
    (direction: 'next' | 'previous') => {
      if (!findingsContext) return;
      const finding =
        direction === 'next'
          ? findingsContext.getNextFinding()
          : findingsContext.getPreviousFinding();
      if (finding) {
        findingsContext.setCurrentFinding(finding);
        setDocument(convertFindingToDocument(finding));
        setMode(ViewerMode.DocumentFullscreen);
      }
    },
    [findingsContext, convertFindingToDocument, setDocument, setMode],
  );

  const hasDocumentNavigation = canNavigatePrevious() || canNavigateNext();

  return (
    <DocumentViewer
      open
      onOpenChange={(details) => {
        if (!details.open) close();
      }}
      src={src}
      fileType={resolveFileType(document, serviceName)}
      fileName={document.name}
      title={document.name}
      showSidebar={false}
      showNavigationActions={isFinding || hasDocumentNavigation}
      previousDisabled={isFinding ? !findingsContext?.getPreviousFinding() : !canNavigatePrevious()}
      nextDisabled={isFinding ? !findingsContext?.getNextFinding() : !canNavigateNext()}
      onPrevious={() => (isFinding ? navigateFinding('previous') : navigateToDocument('previous'))}
      onNext={() => (isFinding ? navigateFinding('next') : navigateToDocument('next'))}
      renderers={renderers}
    />
  );
};

const ReadUrlFullscreenDocumentViewer: FC<{ document: Document }> = ({ document }) => {
  const { readUrl } = useReadUrl(document);

  return <FullscreenDocumentViewer document={document} src={readUrl ?? ''} />;
};

export const DocumentFullscreenContainer = () => {
  const { document } = useViewerContext();

  if (!document) return null;

  const src = resolveDocumentSrc(document);

  if (isIFCDocumentType(document.name) || !src) {
    return <ReadUrlFullscreenDocumentViewer key={document.id} document={document} />;
  }

  return <FullscreenDocumentViewer document={document} src={src} />;
};
